/**
 * AuroraMaterial — procedural aurora curtains (shader wave stage 4, 2026-09-09).
 *
 * The construction every real-time aurora uses (Godot "Volumetric Aurora
 * Borealis", Roy Theunissen's breakdown, Toni Sagristà's "Rendering volume
 * aurorae and nebulae"): a 2D noise field is EXTRUDED DOWNWARD — the curtain
 * is a sheet whose horizontal position is the noise's domain and whose
 * vertical run is a column; a fragment on the curtain mesh marches a few
 * steps DOWN its column accumulating emission, with the altitude colouring
 * of real aurorae (green, 557.7 nm oxygen, low; red/violet high) and a
 * ragged lower edge from the fine noise octave. Additive, unlit, no depth
 * write; the mesh is any vertical band (the caller places it high and far).
 *
 * `auroraColumnCpu` is the column oracle the tests pin.
 */

import * as THREE from 'three/webgpu';
import {
  Fn, uniform, float, vec2, vec3, vec4, uv, time, mx_noise_float, smoothstep, saturate, mix, Loop,
} from 'three/tsl';

export interface AuroraConfig {
  /** March steps down the column (default 12). */
  steps?: number;
  /** Low-altitude colour (default oxygen green). */
  lowColor?: [number, number, number];
  /** High-altitude colour (default violet-red). */
  highColor?: [number, number, number];
  /** Overall intensity (default 1). */
  intensity?: number;
  /** Curtain drift speed (default 0.05). */
  speed?: number;
  /** Horizontal noise scale along the curtain (default 3). */
  scale?: number;
}

export interface AuroraUniforms {
  intensity: { value: number };
  speed: { value: number };
}

/** Curtain density at horizontal x (curtain units), height h 0..1, time t — the shader's rule. */
export function auroraColumnCpu(x: number, h: number, t: number, scale = 3): number {
  const n1 = Math.sin(x * scale * 1.7 + t * 0.6) * 0.5 + 0.5;
  const n2 = Math.sin(x * scale * 5.3 - t * 1.1 + n1 * 3) * 0.5 + 0.5;
  const ray = Math.pow(n1 * 0.6 + n2 * 0.4, 3);
  // vertical envelope: bright band low, fading up; ragged lower edge
  const lowEdge = 0.12 + n2 * 0.18;
  const s = (h - lowEdge) / 0.12;
  const bottom = s < 0 ? 0 : s > 1 ? 1 : s * s * (3 - 2 * s);
  const top = Math.exp(-(h - lowEdge) * 2.2);
  return ray * bottom * top;
}

/** Altitude colour mix: 0 at the curtain foot → green, 1 at the top → high colour. */
export function auroraColorMixCpu(h: number): number {
  const t = (h - 0.25) / 0.6;
  const s = t < 0 ? 0 : t > 1 ? 1 : t;
  return s * s;
}

export function createAuroraMaterial(config?: AuroraConfig): { material: THREE.NodeMaterial; uniforms: AuroraUniforms } {
  const steps = Math.max(4, Math.min(32, config?.steps ?? 12));
  const lc = config?.lowColor ?? [0.25, 1.0, 0.45];
  const hc = config?.highColor ?? [0.75, 0.3, 0.9];
  const uIntensity = uniform(config?.intensity ?? 1);
  const uSpeed = uniform(config?.speed ?? 0.05);
  const scale = config?.scale ?? 3;

  const material = new THREE.MeshBasicNodeMaterial();
  material.transparent = true;
  material.depthWrite = false;
  material.side = THREE.DoubleSide;
  material.blending = THREE.AdditiveBlending;
  material.fog = false;
  material.toneMapped = true;

  material.colorNode = Fn(() => {
    const u0 = uv();
    const t = time.mul(uSpeed).mul(10);
    const acc = vec3(0, 0, 0).toVar();
    const x = u0.x.mul(scale);
    // March down the column from the fragment's height: each step samples
    // the ray field a little lower (the curtain's fold is seen through).
    Loop(steps, ({ i }) => {
      const fi = float(i as never).div(steps);
      const h = u0.y.sub(fi.mul(0.35)).clamp(0, 1);
      const n1 = mx_noise_float(vec2(x.mul(1.7).add(t.mul(0.06)), fi.mul(0.3))).mul(0.5).add(0.5);
      const n2 = mx_noise_float(vec2(x.mul(5.3).sub(t.mul(0.11)).add(n1.mul(3)), h.mul(2))).mul(0.5).add(0.5);
      const ray = n1.mul(0.6).add(n2.mul(0.4)).pow(3);
      const lowEdge = n2.mul(0.18).add(0.12);
      const bottom = smoothstep(lowEdge, lowEdge.add(0.12), h);
      const top = h.sub(lowEdge).mul(-2.2).exp();
      const dens = ray.mul(bottom).mul(top);
      const col = mix(vec3(lc[0], lc[1], lc[2]), vec3(hc[0], hc[1], hc[2]), saturate(h.sub(0.25).div(0.6)).pow(2));
      acc.addAssign(col.mul(dens).div(steps));
    });
    const edge = smoothstep(float(0), float(0.08), u0.x).mul(smoothstep(float(1), float(0.92), u0.x));
    return vec4(acc.mul(uIntensity).mul(2.2).mul(edge), 1);
  })();

  return { material: material as THREE.NodeMaterial, uniforms: { intensity: uIntensity as unknown as { value: number }, speed: uSpeed as unknown as { value: number } } };
}
